import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Home, Building2, Phone, Stethoscope, ChevronRight, X, ArrowUpRight, Download } from "lucide-react";

const startOf = (kind) => {
  const d = new Date();
  if (kind === "semaine") { const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); }
  else if (kind === "mois") d.setDate(1);
  else if (kind === "trimestre") { const q = Math.floor(d.getMonth() / 3) * 3; d.setMonth(q, 1); }
  else if (kind === "annee") d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);
  return d;
};
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const HEADER_COLORS = ["#FEF3C7", "#DBEAFE", "#DCFCE7", "#FCE7F3", "#EDE9FE", "#FFE4E6", "#CCFBF1"];
const typeIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("domicile")) return Home;
  if (n.includes("cabinet")) return Building2;
  if (n.includes("téléph") || n.includes("telephon")) return Phone;
  return Stethoscope;
};

function DashSelect({ testid, value, onChange, placeholder, options }) {
  const applied = !!value;
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="text-[12.5px] px-2.5 py-1.5 rounded-[7px]"
      style={{ border: applied ? "1px solid var(--border-input)" : "1px dashed var(--border-dashed)", background: applied ? "#fff" : "transparent", color: applied ? "var(--ink)" : "var(--muted)" }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

export default function Soins() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [soins, setSoins] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tags, setTags] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [periode, setPeriode] = useState(params.get("periode") || "");
  const [dateFrom, setDateFrom] = useState(params.get("from") || "");
  const [dateTo, setDateTo] = useState(params.get("to") || "");
  const [type, setType] = useState("");
  const [tag, setTag] = useState("");
  const [secteur, setSecteur] = useState("");
  const [apercu, setApercu] = useState(null);

  useEffect(() => {
    api.get("/soins").then((r) => setSoins(r.data)).catch(() => {});
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/settings/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => setCareTypes(r.data)).catch(() => {});
    if (params.get("periode")) setParams({});
    /* eslint-disable-next-line */
  }, []);

  const pmap = useMemo(() => Object.fromEntries(patients.map((p) => [p.id, p])), [patients]);
  const ctmap = useMemo(() => Object.fromEntries(careTypes.map((c) => [c.id, c.nom])), [careTypes]);
  const secmap = useMemo(() => Object.fromEntries(secteurs.map((s) => [s.id, s.nom])), [secteurs]);
  const nowIso = new Date().toISOString();

  const rows = useMemo(() => {
    return soins.filter((s) => {
      const p = pmap[s.patient_id] || {};
      if (type && s.type_soin !== type) return false;
      if (secteur && !(p.secteurs || []).includes(secteur)) return false;
      if (tag && !(p.tags || []).includes(tag)) return false;
      if (periode) {
        const d = s.date ? new Date(s.date) : null;
        if (!d) return false;
        if (periode === "perso") {
          if (dateFrom && d < new Date(dateFrom)) return false;
          if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
        } else if (d < startOf(periode)) return false;
      }
      return true;
    });
  }, [soins, pmap, type, secteur, tag, periode, dateFrom, dateTo]);

  const nbAvenir = rows.filter((s) => s.date > nowIso).length;

  const exportSoins = async (format) => {
    try {
      const res = await api.post("/soins/export", { format, ids: rows.map((s) => s.id) }, { responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : format;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `soins.${ext}`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export généré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export impossible"); }
  };

  const grouped = useMemo(() => {
    const future = rows.filter((s) => s.date > nowIso).sort((a, b) => (a.date < b.date ? -1 : 1));
    const past = rows.filter((s) => s.date <= nowIso).sort((a, b) => (a.date < b.date ? 1 : -1));
    const ordered = [...future, ...past];
    const now = new Date(); const today0 = new Date(now); today0.setHours(0, 0, 0, 0);
    const out = []; let curKey = null; let headerIdx = 0;
    ordered.forEach((s) => {
      const d = new Date(s.date); const d0 = new Date(d); d0.setHours(0, 0, 0, 0);
      let key, label;
      if (s.date > nowIso) { key = "avenir"; label = "À venir"; }
      else {
        const diff = Math.round((today0 - d0) / 86400000);
        if (diff === 0) { key = "today"; label = `Aujourd'hui · ${d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}`; }
        else if (diff < 31) { key = d0.toISOString().slice(0, 10); label = cap(d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })); }
        else { key = `m${d.getFullYear()}-${d.getMonth()}`; label = cap(d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })); }
      }
      if (key !== curKey) { out.push({ header: true, key, label, count: 0, ci: headerIdx++ }); curKey = key; }
      out[out.length - 1].count += 1;
      out.push({ soin: s });
    });
    return out;
  }, [rows, nowIso]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="soin-periode" value={periode} onChange={(e) => setPeriode(e.target.value)} className="sx-input" style={{ width: "auto", height: 34, fontSize: "12.5px" }}>
          <option value="">Toutes dates</option><option value="semaine">Cette semaine</option><option value="mois">Ce mois</option><option value="trimestre">Ce trimestre</option><option value="annee">Cette année</option><option value="perso">Période personnalisée</option>
        </select>
        {periode === "perso" && (
          <>
            <input type="date" data-testid="soin-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sx-input" style={{ width: "auto", height: 34 }} />
            <span style={{ color: "var(--muted-2)" }}>→</span>
            <input type="date" data-testid="soin-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sx-input" style={{ width: "auto", height: 34 }} />
          </>
        )}
        <DashSelect testid="soin-type" value={type} onChange={setType} placeholder="Type" options={careTypes.map((c) => ({ v: c.id, l: c.nom }))} />
        <DashSelect testid="soin-secteur" value={secteur} onChange={setSecteur} placeholder="Secteur" options={secteurs.map((s) => ({ v: s.id, l: s.nom }))} />
        <DashSelect testid="soin-tag" value={tag} onChange={setTag} placeholder="Tags" options={tags.map((t) => ({ v: t.id, l: t.titre }))} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[12.5px]" style={{ color: "var(--muted-2)" }} data-testid="soin-summary">{rows.length} soins · {nbAvenir} à venir</span>
          <div className="flex items-center rounded-[7px] overflow-hidden" style={{ border: "1px solid var(--border-input)" }}>
            <span className="px-2 py-1.5 text-[11.5px] flex items-center gap-1" style={{ color: "var(--muted)", background: "var(--bg-warm)" }}><Download className="w-3.5 h-3.5" />Exporter</span>
            <button data-testid="soin-export-csv" onClick={() => exportSoins("csv")} className="px-2.5 py-1.5 text-[12px] hover:bg-slate-50" style={{ color: "var(--ink)", borderLeft: "1px solid var(--border-input)" }}>CSV</button>
            <button data-testid="soin-export-xlsx" onClick={() => exportSoins("xlsx")} className="px-2.5 py-1.5 text-[12px] hover:bg-slate-50" style={{ color: "var(--ink)", borderLeft: "1px solid var(--border-input)" }}>Excel</button>
            <button data-testid="soin-export-pdf" onClick={() => exportSoins("pdf")} className="px-2.5 py-1.5 text-[12px] hover:bg-slate-50" style={{ color: "var(--ink)", borderLeft: "1px solid var(--border-input)" }}>PDF</button>
          </div>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 sx-card overflow-hidden">
          <table className="w-full text-[13.5px]">
            <tbody>
              {grouped.length === 0 && <tr><td className="px-4 py-12 text-center" style={{ color: "var(--faint)" }}>Aucun soin.</td></tr>}
              {grouped.map((g, i) => g.header ? (
                <tr key={`h-${g.key}-${i}`}>
                  <td colSpan={apercu ? 3 : 5} className="px-4 py-2 text-[11.5px] font-bold" style={{ color: "var(--ink)", background: HEADER_COLORS[g.ci % HEADER_COLORS.length], borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{g.label} <span className="mono ml-1" style={{ color: "var(--muted)" }}>{g.count}</span></td>
                </tr>
              ) : (() => {
                const s = g.soin; const p = pmap[s.patient_id] || {}; const sel = apercu === s.id;
                const tn = ctmap[s.type_soin] || (s.type_soin === "soin_cabinet" ? "Soin au cabinet" : s.type_soin || "Soin");
                const TI = typeIcon(tn);
                return (
                  <tr key={s.id} data-testid={`soin-row-${s.id}`} className="cursor-pointer transition-colors align-top"
                    style={{ borderTop: "1px solid var(--divider)", background: sel ? "var(--row-selected)" : "", boxShadow: sel ? "inset 3px 0 0 var(--accent)" : "" }}
                    onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = "var(--row-hover)"; }}
                    onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = ""; }}
                    onClick={() => setApercu(s.id)}>
                    <td className="px-4 py-2.5 whitespace-nowrap mono text-[12px]" style={{ color: "var(--text-2)", width: 70 }}>{s.date ? new Date(s.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold" style={{ color: "var(--ink)" }}>{p.prenom || ""} {p.nom || "—"}</div>
                      <div className="mono text-[10.5px]" style={{ color: "var(--id)" }}>{s.id}</div>
                    </td>
                    <td className="px-4 py-2.5" style={{ color: s.titre ? "var(--ink)" : "var(--faint)" }}>{s.titre || "Sans titre"}</td>
                    {!apercu && (
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        <span className="inline-flex items-center gap-2" style={{ color: "var(--text-2)" }}>
                          <span className="inline-flex items-center justify-center" style={{ width: 26, height: 26, background: "var(--bg-warm)", borderRadius: 7 }}><TI className="w-[15px] h-[15px]" style={{ color: "var(--muted)" }} /></span>
                          {tn}
                        </span>
                      </td>
                    )}
                    {!apercu && <td className="px-4 py-2.5 hidden lg:table-cell" style={{ color: (p.secteurs || []).length ? "var(--text-2)" : "var(--faint)" }}>{(p.secteurs || []).map((sid) => secmap[sid]).filter(Boolean).join(", ") || "—"}</td>}
                    <td className="px-4 py-2.5" style={{ width: 40 }}><ChevronRight className="w-4 h-4" style={{ color: "var(--muted-2)" }} /></td>
                  </tr>
                );
              })())}
            </tbody>
          </table>
        </div>
        {apercu && <ApercuSoin key={apercu} sid={apercu} onClose={() => setApercu(null)} nav={nav} pmap={pmap} ctmap={ctmap} />}
      </div>
    </div>
  );
}

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

function ApercuSoin({ sid, onClose, nav, pmap, ctmap }) {
  const [s, setS] = useState(null);
  const [fact, setFact] = useState(null);
  useEffect(() => {
    api.get(`/soins/${sid}`).then((r) => setS(r.data)).catch(() => setS(null));
    api.get(`/soins/${sid}/facture`).then((r) => setFact(r.data)).catch(() => {});
  }, [sid]);
  if (!s) return <aside className="w-[420px] shrink-0 sx-card p-5" style={{ borderRadius: 14 }}><div style={{ color: "var(--faint)" }}>Chargement…</div></aside>;
  const p = pmap[s.patient_id] || {};
  const tn = ctmap[s.type_soin] || (s.type_soin === "soin_cabinet" ? "Soin au cabinet" : s.type_soin || "Soin");
  const note = (s.contenu_md || "").replace(/[#*_`>\-]/g, "").replace(/\s+/g, " ").trim().slice(0, 180);
  const solde = fact ? fact.solde : null;
  const regl = solde === null ? null : (solde <= 0.009 ? { label: "Soldée", cls: "sx-badge-green" } : (fact.total_paye > 0 ? { label: "Partielle", cls: "sx-badge-amber" } : { label: "Non soldée", cls: "sx-badge-red" }));
  const Pill = ({ children }) => <span className="sx-info">{children}</span>;
  return (
    <aside className="w-[420px] shrink-0 sx-card p-5 sticky self-start" style={{ borderRadius: 14, boxShadow: "var(--sh-float)", top: "calc(var(--header-h, 96px) + 16px)", maxHeight: "calc(100vh - var(--header-h, 96px) - 32px)", overflowY: "auto" }} data-testid="apercu-soin">
      <div className="flex items-center justify-between text-[12.5px]">
        <button data-testid="apercu-soin-close" onClick={onClose} className="inline-flex items-center gap-1" style={{ color: "var(--muted)" }}><X className="w-3.5 h-3.5" />Esc</button>
        <button onClick={() => nav(`/fiche-soin/${s.id}`)} className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--ink)" }}>Ouvrir la fiche ↗</button>
      </div>
      <div className="mt-3 text-[22px] font-bold tracking-[-0.01em]" style={{ color: s.titre ? "var(--ink)" : "var(--faint)" }}>{s.titre || "Sans titre"}</div>
      <div className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>{p.prenom} {p.nom}</div>
      <div className="mono text-[11px] mt-1" style={{ color: "var(--id)" }}>{s.id}</div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        <Pill>{s.date ? new Date(s.date).toLocaleDateString("fr-FR") : "—"}</Pill>
        <Pill>{tn}</Pill>
        {regl && <span className={`sx-badge ${regl.cls}`}>{regl.label}</span>}
      </div>
      {fact && <div className="mt-3 text-[13px]" style={{ color: "var(--text-2)" }}>Total : <span className="font-semibold" style={{ color: "var(--ink)" }}>{eur(fact.total_ttc)}</span> · Réglé {eur(fact.total_paye)} · Solde {eur(fact.solde)}</div>}
      <div className="mt-3">
        <div className="text-[11.5px] font-medium mb-1" style={{ color: "var(--muted-2)" }}>NOTE</div>
        <div className="text-[13px]" style={{ color: note ? "var(--text-2)" : "var(--faint)" }}>{note || "Aucune note."}</div>
      </div>
      <button data-testid="apercu-soin-open" onClick={() => nav(`/fiche-soin/${s.id}`)} className="sx-btn-dark w-full justify-center mt-4">Ouvrir la fiche de soin</button>
    </aside>
  );
}
